import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CornerDownLeft, Search, X, ChevronRight } from 'lucide-react';
import type { ZoekGroep, ZoekItem } from './zoekTypes';
import { markeerDelen, scoreItem, zoekTokens } from './zoekMatch';

export type UitvoerModus = 'erheen' | 'doe';

interface ZoekVensterProps {
  items: ZoekItem[];
  recent: ZoekItem[];
  voorbeelden: string[];
  /** Laatste redmiddel als niets matcht, bv. "Vraag de klantenservice: …". */
  vraagItems: (vraag: string) => ZoekItem[];
  onUitvoeren: (item: ZoekItem, modus: UitvoerModus) => void;
  onSluit: () => void;
}

const GROEP_LIMIET: Record<ZoekGroep, number> = {
  recent: 5,
  taken: 6,
  paginas: 6,
  opDezePagina: 8,
  elders: 6,
  acties: 5,
  vraag: 2,
};

const GROEP_DEFAULT: Record<ZoekGroep, string> = {
  recent: 'Recent',
  taken: 'Taken',
  paginas: "Pagina's",
  opDezePagina: 'Op dit scherm',
  elders: "Knoppen op andere pagina's",
  acties: 'Acties',
  vraag: 'Hulp',
};

const isMac =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad/.test(navigator.platform);
const MOD = isMac ? '⌘' : 'Ctrl';

const kanErheen = (item: ZoekItem) => !!(item.href || item.element);
const kanDoen = (item: ZoekItem) =>
  !item.alleenAanwijzen && !!(item.actie || item.element || item.knopTekst);

const Kbd: React.FC<{ children: React.ReactNode; licht?: boolean }> = ({
  children,
  licht,
}) => (
  <kbd
    className={`inline-flex min-w-[1.5rem] items-center justify-center rounded-md border px-1.5 py-0.5 text-[11px] font-semibold ${
      licht
        ? 'border-white/30 bg-white/10 text-white'
        : 'border-gray-200 bg-gray-50 text-gray-500'
    }`}
  >
    {children}
  </kbd>
);

const Kruimelpad: React.FC<{ pad?: string[]; className?: string }> = ({
  pad,
  className = '',
}) =>
  pad?.length ? (
    <span className={`truncate text-gray-400 ${className}`}>
      {pad.map((deel, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="mx-1 text-gray-300">/</span>}
          {deel}
        </React.Fragment>
      ))}
    </span>
  ) : null;

/**
 * Zoek-alles-venster (Ctrl K): pagina's, taken met stappen, knoppen op het
 * scherm en acties. Links de resultaten, rechts hoe je het doet, met
 * "Breng me erheen" (↵) en "Doe het" (Ctrl ↵).
 */
export const ZoekVenster: React.FC<ZoekVensterProps> = ({
  items,
  recent,
  voorbeelden,
  vraagItems,
  onUitvoeren,
  onSluit,
}) => {
  const { t } = useTranslation();
  const [vraag, setVraag] = useState('');
  const [actief, setActief] = useState(0);
  // Zonder zoekvraag tonen we voorbeelden, tot de gebruiker zelf een item aanwijst.
  const [aangewezen, setAangewezen] = useState(false);
  const invoerRef = useRef<HTMLInputElement>(null);
  const lijstRef = useRef<HTMLDivElement>(null);

  const tokens = useMemo(() => zoekTokens(vraag), [vraag]);

  const groepen = useMemo<
    Array<{ groep: ZoekGroep; items: Array<ZoekItem & { score: number }> }>
  >(() => {
    if (!vraag.trim()) {
      const leeg: ZoekGroep[] = ['recent', 'opDezePagina', 'acties'];
      return leeg
        .map(groep => ({
          groep,
          items: (groep === 'recent'
            ? recent
            : items.filter(i => i.groep === groep)
          )
            .slice(0, GROEP_LIMIET[groep])
            .map(i => ({ ...i, score: 0 })),
        }))
        .filter(g => g.items.length);
    }

    const perGroep = new Map<ZoekGroep, Array<ZoekItem & { score: number }>>();
    for (const item of items) {
      const score = scoreItem(item, tokens, vraag);
      if (score <= 0) continue;
      const lijst = perGroep.get(item.groep) ?? [];
      lijst.push({ ...item, score });
      perGroep.set(item.groep, lijst);
    }
    const resultaat = [...perGroep.entries()]
      .map(([groep, lijst]) => ({
        groep,
        items: lijst
          .sort((a, b) => b.score - a.score)
          .slice(0, GROEP_LIMIET[groep]),
      }))
      // Groep met de beste treffer bovenaan
      .sort((a, b) => b.items[0].score - a.items[0].score);

    const hulp = vraagItems(vraag).map(i => ({ ...i, score: 0 }));
    if (hulp.length) resultaat.push({ groep: 'vraag', items: hulp });
    return resultaat;
  }, [items, recent, tokens, vraag, vraagItems]);

  const plat = useMemo(() => groepen.flatMap(g => g.items), [groepen]);
  const geselecteerd = plat[Math.min(actief, plat.length - 1)];

  useEffect(() => {
    setActief(0);
    setAangewezen(false);
  }, [vraag]);

  const wijsAan = (i: number) => {
    setActief(i);
    setAangewezen(true);
  };

  useEffect(() => {
    invoerRef.current?.focus();
    const vorige = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = vorige;
    };
  }, []);

  useEffect(() => {
    lijstRef.current
      ?.querySelector<HTMLElement>(`[data-index="${actief}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [actief]);

  const voerUit = (item: ZoekItem | undefined, modus: UitvoerModus) => {
    if (!item) return;
    if (modus === 'doe' && !kanDoen(item)) modus = 'erheen';
    if (modus === 'erheen' && !kanErheen(item)) modus = 'doe';
    onUitvoeren(item, modus);
  };

  const onToets = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      wijsAan(plat.length ? (aangewezen ? (actief + 1) % plat.length : 0) : 0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      wijsAan(plat.length ? (actief - 1 + plat.length) % plat.length : 0);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      voerUit(geselecteerd, e.ctrlKey || e.metaKey ? 'doe' : 'erheen');
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onSluit();
    }
  };

  // "Bedoel je": de volgende beste treffers buiten het geselecteerde item
  const bedoelJe =
    vraag.trim() && geselecteerd
      ? plat
          .filter(i => i.id !== geselecteerd.id && i.groep !== 'vraag')
          .slice(0, 3)
      : [];

  const groepLabel = (groep: ZoekGroep) =>
    t(`zoeken.groep.${groep}`, { defaultValue: GROEP_DEFAULT[groep] });

  let index = -1;

  return (
    <div
      data-zoek-negeer
      className="fixed inset-0 z-[60] flex items-start justify-center bg-navy-950/50 p-3 pt-[6vh] backdrop-blur-sm sm:p-6 sm:pt-[8vh] print:hidden"
      onMouseDown={e => {
        if (e.target === e.currentTarget) onSluit();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('zoeken.titel', { defaultValue: 'Zoek in BeLagom' })}
        className="flex h-[min(40rem,86vh)] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
        onKeyDown={onToets}
      >
        {/* Zoekbalk */}
        <div className="flex items-center gap-3 border-b border-gray-100 px-5 py-4">
          <Search size={20} className="shrink-0 text-navy-400" />
          <input
            ref={invoerRef}
            value={vraag}
            onChange={e => setVraag(e.target.value)}
            placeholder={t('zoeken.placeholder', {
              defaultValue:
                'Zoek of vraag iets, bijvoorbeeld: factuur aanmaken',
            })}
            className="min-w-0 flex-1 bg-transparent text-base text-navy-900 placeholder:text-gray-400 focus:outline-none"
            role="combobox"
            aria-expanded="true"
            aria-controls="zoek-resultaten"
            aria-activedescendant={
              geselecteerd ? `zoek-item-${actief}` : undefined
            }
            autoComplete="off"
            spellCheck={false}
          />
          {vraag && (
            <button
              type="button"
              onClick={() => {
                setVraag('');
                invoerRef.current?.focus();
              }}
              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label={t('zoeken.wis', { defaultValue: 'Zoekvraag wissen' })}
            >
              <X size={16} />
            </button>
          )}
          <button
            type="button"
            onClick={onSluit}
            className="hidden rounded-md sm:block"
            aria-label={t('zoeken.sluiten', { defaultValue: 'Sluiten' })}
          >
            <Kbd>Esc</Kbd>
          </button>
        </div>

        <div className="flex min-h-0 flex-1">
          {/* Resultaten */}
          <div
            ref={lijstRef}
            id="zoek-resultaten"
            role="listbox"
            className="min-h-0 w-full overflow-y-auto border-gray-100 py-2 md:w-[52%] md:border-r"
          >
            {groepen.map(g => (
              <div key={g.groep} className="mb-2">
                <div className="px-5 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
                  {groepLabel(g.groep)}
                </div>
                {g.items.map(item => {
                  index += 1;
                  const i = index;
                  const isActief = i === actief;
                  const Icoon = item.icoon ?? ChevronRight;
                  return (
                    <div
                      key={`${g.groep}-${item.id}`}
                      id={`zoek-item-${i}`}
                      data-index={i}
                      role="option"
                      aria-selected={isActief}
                      onMouseMove={() =>
                        (actief !== i || !aangewezen) && wijsAan(i)
                      }
                      onClick={e =>
                        voerUit(item, e.ctrlKey || e.metaKey ? 'doe' : 'erheen')
                      }
                      className={`mx-2 flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 ${
                        isActief ? 'bg-navy-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <Icoon
                        size={17}
                        className={`shrink-0 ${isActief ? 'text-navy-700' : 'text-gray-400'}`}
                      />
                      <div className="flex min-w-0 flex-1 items-baseline gap-2">
                        <span className="shrink-0 truncate text-sm text-navy-900 max-w-[70%]">
                          {markeerDelen(item.titel, tokens).map((d, j) => (
                            <span
                              key={j}
                              className={
                                d.match || !vraag
                                  ? 'font-semibold'
                                  : 'font-normal'
                              }
                            >
                              {d.tekst}
                            </span>
                          ))}
                        </span>
                        <Kruimelpad
                          pad={item.pad}
                          className="min-w-0 text-xs"
                        />
                      </div>
                      {isActief && (
                        <span className="hidden shrink-0 md:inline-flex">
                          <Kbd>
                            <CornerDownLeft size={11} />
                          </Kbd>
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}

            {!plat.length && (
              <div className="px-6 py-10 text-center text-sm text-gray-500">
                {vraag.trim()
                  ? t('zoeken.geenResultaten', {
                      defaultValue:
                        'Niets gevonden voor "{{vraag}}". Probeer een ander woord.',
                      vraag,
                    })
                  : t('zoeken.leeg', {
                      defaultValue:
                        'Typ om te zoeken in pagina’s, taken en knoppen.',
                    })}
              </div>
            )}
          </div>

          {/* Detail: hoe doe je het */}
          <div className="hidden min-h-0 flex-1 overflow-y-auto bg-gray-50/60 px-7 py-6 md:block">
            {geselecteerd && (vraag.trim() || aangewezen) ? (
              <Detail
                item={geselecteerd}
                bedoelJe={bedoelJe}
                onUitvoeren={voerUit}
                onKiesBedoelJe={id => wijsAan(plat.findIndex(i => i.id === id))}
              />
            ) : (
              <Voorbeelden voorbeelden={voorbeelden} onKies={setVraag} />
            )}
          </div>
        </div>

        {/* Toetsen */}
        <div className="hidden items-center gap-5 border-t border-gray-100 px-5 py-2.5 text-xs text-gray-500 sm:flex">
          <span className="flex items-center gap-1.5">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd> {t('zoeken.toets.kiezen', { defaultValue: 'kiezen' })}
          </span>
          <span className="flex items-center gap-1.5">
            <Kbd>
              <CornerDownLeft size={11} />
            </Kbd>{' '}
            {t('zoeken.toets.erheen', { defaultValue: 'breng me erheen' })}
          </span>
          <span className="flex items-center gap-1.5">
            <Kbd>{MOD}</Kbd>
            <Kbd>
              <CornerDownLeft size={11} />
            </Kbd>{' '}
            {t('zoeken.toets.doe', { defaultValue: 'doe het' })}
          </span>
          <span className="flex items-center gap-1.5">
            <Kbd>Esc</Kbd>{' '}
            {t('zoeken.toets.sluiten', { defaultValue: 'sluiten' })}
          </span>
          <span className="ml-auto flex items-center gap-1.5">
            <Kbd>{MOD}</Kbd>
            <Kbd>K</Kbd>{' '}
            {t('zoeken.toets.openen', { defaultValue: 'zoeken openen' })}
          </span>
        </div>
      </div>
    </div>
  );
};

const Voorbeelden: React.FC<{
  voorbeelden: string[];
  onKies: (v: string) => void;
}> = ({ voorbeelden, onKies }) => {
  const { t } = useTranslation();
  return (
    <div>
      <div className="flex items-start gap-3">
        <Search size={20} className="mt-1 shrink-0 text-navy-400" />
        <div>
          <h2 className="text-xl font-semibold text-navy-900">
            {t('zoeken.watVragen', { defaultValue: 'Wat je kunt vragen' })}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {t('zoeken.watVragenUitleg', {
              defaultValue:
                'Typ een pagina, een knop of gewoon wat je wilt doen. Een hele zin of vraag mag ook.',
            })}
          </p>
        </div>
      </div>
      {voorbeelden.length > 0 && (
        <>
          <div className="mb-1 mt-6 text-xs font-semibold uppercase tracking-wide text-gray-400">
            {t('zoeken.bijvoorbeeld', { defaultValue: 'Bijvoorbeeld' })}
          </div>
          <div className="divide-y divide-gray-100 border-y border-gray-100">
            {voorbeelden.map(v => (
              <button
                key={v}
                type="button"
                onClick={() => onKies(v)}
                className="block w-full px-2 py-3 text-left text-sm text-navy-900 hover:bg-white"
              >
                {v}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const Detail: React.FC<{
  item: ZoekItem;
  bedoelJe: ZoekItem[];
  onUitvoeren: (item: ZoekItem, modus: UitvoerModus) => void;
  onKiesBedoelJe: (id: string) => void;
}> = ({ item, bedoelJe, onUitvoeren, onKiesBedoelJe }) => {
  const { t } = useTranslation();
  const Icoon = item.icoon ?? ChevronRight;
  const erheen = kanErheen(item);
  const doen = kanDoen(item);

  return (
    <div>
      <div className="flex items-start gap-3">
        <Icoon size={20} className="mt-1 shrink-0 text-navy-500" />
        <div className="min-w-0">
          <h2 className="text-xl font-semibold text-navy-900">{item.titel}</h2>
          <Kruimelpad pad={item.pad} className="mt-0.5 block text-sm" />
          {item.omschrijving && (
            <p className="mt-2 text-sm text-gray-600">{item.omschrijving}</p>
          )}
        </div>
      </div>

      {!!item.stappen?.length && (
        <>
          <div className="mb-1 mt-6 text-xs font-semibold uppercase tracking-wide text-gray-400">
            {t('zoeken.zoDoeJeHet', { defaultValue: 'Zo doe je het' })}
          </div>
          <ol className="divide-y divide-gray-100 border-b border-gray-100">
            {item.stappen.map((stap, i) => (
              <li key={i} className="flex gap-3 py-2.5 text-sm text-navy-900">
                <span className="w-4 shrink-0 text-gray-400">{i + 1}.</span>
                <span>{stap}</span>
              </li>
            ))}
          </ol>
        </>
      )}

      <div className="mt-6 flex flex-wrap gap-3">
        {erheen && (
          <button
            type="button"
            onClick={() => onUitvoeren(item, 'erheen')}
            className="inline-flex items-center gap-2 rounded-xl bg-navy-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-navy-700"
          >
            {item.element
              ? t('zoeken.laatZien', { defaultValue: 'Laat zien waar' })
              : t('zoeken.brengMeErheen', { defaultValue: 'Breng me erheen' })}
            <Kbd licht>
              <CornerDownLeft size={11} />
            </Kbd>
          </button>
        )}
        {doen && (
          <button
            type="button"
            onClick={() => onUitvoeren(item, 'doe')}
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold ${
              erheen
                ? 'border border-navy-200 bg-white text-navy-900 hover:bg-navy-50'
                : 'bg-navy-900 text-white hover:bg-navy-700'
            }`}
          >
            {t('zoeken.doeHet', { defaultValue: 'Doe het' })}
            {erheen ? (
              <>
                <Kbd>{MOD}</Kbd>
                <Kbd>
                  <CornerDownLeft size={11} />
                </Kbd>
              </>
            ) : (
              <Kbd licht>
                <CornerDownLeft size={11} />
              </Kbd>
            )}
          </button>
        )}
      </div>

      {bedoelJe.length > 0 && (
        <>
          <div className="mb-1 mt-8 text-xs font-semibold uppercase tracking-wide text-gray-400">
            {t('zoeken.bedoelJe', { defaultValue: 'Bedoel je' })}
          </div>
          <div className="divide-y divide-gray-100 border-y border-gray-100">
            {bedoelJe.map(b => {
              const BIcoon = b.icoon ?? ChevronRight;
              return (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => onKiesBedoelJe(b.id)}
                  className="flex w-full items-center gap-3 px-2 py-2.5 text-left text-sm hover:bg-white"
                >
                  <BIcoon size={15} className="shrink-0 text-gray-400" />
                  <span className="shrink-0 font-semibold text-navy-900">
                    {b.titel}
                  </span>
                  <Kruimelpad pad={b.pad} className="min-w-0 text-xs" />
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
